import * as React from 'react';
import { Link } from 'gatsby';

import { supportedLocales, type LocaleAlternate, type LocaleCode } from '../../config/locales';
import { getLocaleLabel } from '../../config/localeLabels';
import { persistLocale } from '../../storyblok/locales/persistedLocale';
import { Globe } from './icons/Globe';

export type LanguageSelectorVariant = 'desktop' | 'mobile';

export interface LanguageSelectorProps {
  variant: LanguageSelectorVariant;
  currentLocale: LocaleCode;
  alternates: readonly LocaleAlternate[];
  className?: string;
  /**
   * Test-only hook: when `true`, the panel renders as if open on first paint.
   * Used by SSR snapshot tests (the Vitest environment runs under Node with no
   * DOM, so simulated user clicks are not possible). Production code must never
   * set this prop; the component's open/closed state is fully owned internally.
   */
  __forceOpen?: boolean;
}

const supportedLocaleSet: ReadonlySet<LocaleCode> = new Set(supportedLocales);

const isSupportedLocale = (code: string): code is LocaleCode =>
  supportedLocaleSet.has(code as LocaleCode);

// Trigger and option text colors are inherited from the parent (via `text-current`)
// so the header can theme the selector ice-blue over the dark hero, gray-700 over
// the scrolled white header, and ice-blue inside the dark slide-in menu — without
// the selector itself knowing about those states.
const focusRingClasses =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current';

const ChevronDown: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    aria-hidden="true"
    data-icon="chevron-down"
    viewBox="0 0 24 24"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

type OptionRowProps = {
  lang: LocaleCode;
  path: string;
  isCurrent: boolean;
  optionId: string;
  index: number;
  onCurrentSelected: (event: React.MouseEvent | React.KeyboardEvent) => void;
  onOptionKeyDown: (event: React.KeyboardEvent<HTMLAnchorElement>) => void;
};

const OptionRow: React.FC<OptionRowProps> = ({
  lang,
  path,
  isCurrent,
  optionId,
  index,
  onCurrentSelected,
  onOptionKeyDown,
}) => {
  // Outer <li> carries semantic role + the active-row visual treatment so the
  // SSR markup snapshot is structurally clean. The inner <a> handles focus and
  // hit area.
  const rowClasses = [
    isCurrent ? 'bg-gray-200 rounded-sm font-bold' : 'rounded-xs font-medium',
  ].join(' ');

  const linkClasses = [
    'flex w-full items-center justify-center p-1',
    'font-primary text-sm text-gray-700',
    isCurrent ? 'rounded-sm' : 'rounded-xs',
    focusRingClasses,
  ].join(' ');

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    // Persist the chosen locale BEFORE the same-locale guard so the write
    // fires for both current-locale clicks (navigation prevented by
    // `onCurrentSelected`) and non-current-locale clicks (Gatsby's default
    // Link navigation continues after this synchronous call).
    persistLocale(lang);
    if (isCurrent) {
      onCurrentSelected(event);
    }
  };

  return (
    <li
      role="option"
      id={optionId}
      aria-current={isCurrent ? 'true' : undefined}
      aria-selected={isCurrent}
      data-option-index={index}
      className={rowClasses}
    >
      <Link
        to={path}
        lang={lang}
        hrefLang={lang}
        tabIndex={-1}
        className={linkClasses}
        onClick={handleClick}
        onKeyDown={onOptionKeyDown}
      >
        {getLocaleLabel(lang)}
      </Link>
    </li>
  );
};

export function LanguageSelector(props: LanguageSelectorProps): JSX.Element | null {
  const { variant, currentLocale, alternates, className, __forceOpen } = props;

  // Defensively filter out alternates whose lang is not a member of
  // supportedLocales — protects against a stale or out-of-band
  // `pageContext.alternates` payload (R7.3).
  const safeAlternates = React.useMemo(
    () => alternates.filter((alt) => isSupportedLocale(alt.lang)),
    [alternates]
  );

  // Order options by supportedLocales declaration order, looking up the
  // alternate path for each locale. If a locale has no alternate, omit it.
  const orderedOptions = React.useMemo(
    () =>
      supportedLocales
        .map((lang) => {
          const alt = safeAlternates.find((a) => a.lang === lang);
          return alt ? { lang, path: alt.path } : null;
        })
        .filter((entry): entry is { lang: LocaleCode; path: string } => entry !== null),
    [safeAlternates]
  );

  const reactId = React.useId();
  const triggerId = `${reactId}-trigger`;
  const listboxId = `${reactId}-listbox`;
  const optionId = (lang: LocaleCode): string => `${reactId}-option-${lang}`;

  const [isOpen, setIsOpen] = React.useState<boolean>(Boolean(__forceOpen));
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const panelRef = React.useRef<HTMLUListElement | null>(null);

  const getOptionAnchors = React.useCallback((): HTMLAnchorElement[] => {
    const panel = panelRef.current;
    if (!panel) {
      return [];
    }
    return Array.from(panel.querySelectorAll<HTMLAnchorElement>('li[role="option"] > a'));
  }, []);

  const close = React.useCallback((restoreFocus: boolean): void => {
    setIsOpen(false);
    if (restoreFocus) {
      // Defer focus restoration so the dismissed element has unmounted.
      window.requestAnimationFrame(() => {
        triggerRef.current?.focus();
      });
    }
  }, []);

  // Outside-click dismissal (R10.1). Scoped to the trigger + panel refs so the
  // mobile variant nested inside the slide-in menu does not catch unrelated
  // taps on the menu itself.
  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleMouseDown = (event: MouseEvent): void => {
      const target = event.target as Node | null;
      if (!target) {
        return;
      }
      if (triggerRef.current?.contains(target)) {
        return;
      }
      if (panelRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen]);

  // Hide-on-single-variant: must run AFTER hooks above to keep the hook order
  // stable across renders. The render itself short-circuits to `null`.
  if (orderedOptions.length < 2) {
    return null;
  }

  const focusOptionAtIndex = (index: number): void => {
    const anchors = getOptionAnchors();
    const total = anchors.length;
    if (total === 0) {
      return;
    }
    const wrapped = ((index % total) + total) % total;
    anchors[wrapped]?.focus();
  };

  const handleTriggerClick = (): void => {
    setIsOpen((prev) => !prev);
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      }
      // After open, focus the first option on the next frame.
      window.requestAnimationFrame(() => {
        focusOptionAtIndex(0);
      });
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      }
      window.requestAnimationFrame(() => {
        focusOptionAtIndex(orderedOptions.length - 1);
      });
      return;
    }
    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      close(true);
    }
  };

  const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>): void => {
    const currentTarget = event.currentTarget;
    const anchors = getOptionAnchors();
    const index = anchors.indexOf(currentTarget);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusOptionAtIndex(index + 1);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusOptionAtIndex(index - 1);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key === 'Tab') {
      // Let the browser advance focus naturally; just close the panel.
      setIsOpen(false);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      // The default behavior for an <a> only fires on Enter (not Space). Click
      // the anchor explicitly so Space activates the same navigation path.
      // The anchor's own onClick will handle the same-locale guard.
      event.preventDefault();
      currentTarget.click();
    }
  };

  const handleCurrentSelected = (event: React.MouseEvent | React.KeyboardEvent): void => {
    // Selecting the row whose locale equals currentLocale closes the panel
    // without navigating (R6.3).
    event.preventDefault();
    close(true);
  };

  const triggerLabel = getLocaleLabel(currentLocale);
  const ariaLabel = `Select language. Current language: ${triggerLabel}.`;

  // Panel: white card with the Figma drop-shadow token, 8px radius, 16px
  // padding (`p-4`), 2px row gap (`gap-0.5`). Desktop floats under the
  // trigger; mobile sits in-flow under the trigger inside the slide-in menu.
  // `text-gray-700` is set explicitly so the floating menu always renders with
  // dark text on its white background, even when the parent wraps the selector
  // in a contrasting color context (e.g. ice-blue inside the mobile drawer).
  const panelBaseClasses =
    'flex flex-col gap-0.5 rounded-md bg-neutral-white p-4 text-gray-700 shadow-dropdown';
  const panelClasses =
    variant === 'desktop'
      ? `${panelBaseClasses} absolute right-0 top-full z-10 mt-2 min-w-[159px]`
      : `${panelBaseClasses} mt-4 w-full`;

  // Trigger color is inherited from the parent (`text-current`). Desktop is a
  // compact inline button (icon + label). Mobile renders the Figma "input
  // /language" pattern: a 24px globe outside a bordered ice-blue input area
  // containing the label and a chevron that rotates 180° when open.
  const sharedTriggerClasses = `bg-transparent text-current ${focusRingClasses} ${
    className ?? ''
  }`.trim();

  const renderDesktopTrigger = (): JSX.Element => (
    <button
      type="button"
      ref={triggerRef}
      id={triggerId}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-controls={isOpen ? listboxId : undefined}
      aria-label={ariaLabel}
      onClick={handleTriggerClick}
      onKeyDown={handleTriggerKeyDown}
      className={`inline-flex items-center gap-2 rounded-sm font-primary text-sm font-medium ${sharedTriggerClasses}`}
    >
      <Globe className="size-6" />
      <span>{triggerLabel}</span>
    </button>
  );

  const renderMobileTrigger = (): JSX.Element => (
    <button
      type="button"
      ref={triggerRef}
      id={triggerId}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-controls={isOpen ? listboxId : undefined}
      aria-label={ariaLabel}
      onClick={handleTriggerClick}
      onKeyDown={handleTriggerKeyDown}
      className={`flex w-full items-center gap-2 ${sharedTriggerClasses}`}
    >
      <Globe className="size-6 shrink-0" />
      <span className="flex flex-1 items-center justify-between rounded-sm border border-current p-2">
        <span className="font-primary text-base font-medium">{triggerLabel}</span>
        <ChevronDown
          className={`size-4 shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </span>
    </button>
  );

  const wrapperClasses = variant === 'desktop' ? 'relative inline-block' : 'relative w-full';

  return (
    <div className={wrapperClasses}>
      {variant === 'desktop' ? renderDesktopTrigger() : renderMobileTrigger()}

      {isOpen && (
        <ul
          ref={panelRef}
          id={listboxId}
          role="listbox"
          aria-labelledby={triggerId}
          tabIndex={-1}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              close(true);
            }
          }}
          className={panelClasses}
        >
          {orderedOptions.map((option, index) => (
            <OptionRow
              key={option.lang}
              lang={option.lang}
              path={option.path}
              isCurrent={option.lang === currentLocale}
              optionId={optionId(option.lang)}
              index={index}
              onCurrentSelected={handleCurrentSelected}
              onOptionKeyDown={handleOptionKeyDown}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
